import { Card, spacing } from '@scality/core-ui';
import { Box } from '@scality/core-ui/dist/next';
import type { ReactNode } from 'react';
import { useState } from 'react';
import styled from 'styled-components';
import { useCanSplitLayout } from '../../dualPanel/hooks/useCanSplitLayout';
import { useIsInPanel } from '../../dualPanel/InPanelContext';
import {
  type ExportTable,
  revealActionsOnHover,
  WidgetActionButton,
  WidgetActions,
  WidgetExportMenu,
} from '../widget-actions';
import { ChartExpandModal } from './ChartExpandModal';

/** Plot height of a chart sitting in its card. */
const DEFAULT_CHART_HEIGHT = 175;

/** How a chart is being drawn — the card hands this to the widget, which lays itself out from it. */
export type ChartView = {
  chartHeight: number;
  /** The card's controls, for core-ui's `rightTitle` slot; only the widget knows where that is. */
  actions: ReactNode;
};

/**
 * core-ui lays the chart title out in the row directly above the plot, where it wraps to a second
 * line at panel widths. Reserving both lines keeps side-by-side charts on a common baseline
 * instead of starting each plot wherever its own title happened to end.
 */
const ChartCardBody = styled(Card.Body)`
  padding: 0.7rem;
  /* A series label is often a bare node or deployment id: one unbreakable word, which would
     otherwise set the card's min-content width and push it past the cell holding it. */
  overflow-wrap: anywhere;

  *:has(+ .recharts-responsive-container) {
    min-height: 2.875rem;
  }

  /* The header reserves the controls' space, so fading them in on hover moves nothing. */
  ${revealActionsOnHover}
`;

/**
 * Plot above, legend under it — the same stack enlarged as in a card. Charts on a dashboard are
 * read side by side, and a legend that moves to a column beside the plot takes the width the plot
 * was being compared on.
 *
 * A `Box` rather than a `Stack`: the stack has to stretch its children, where a `Stack` centres
 * them, and the plot needs `flex`/`minWidth` sizing that `Stack` cannot carry.
 */
export const ChartBody = ({ legend, children }: { legend: ReactNode; children: ReactNode }) => (
  <Box display="flex" flexDirection="column" alignItems="stretch" gap={spacing.r16}>
    <Box flex="1" minWidth={0}>
      {children}
    </Box>
    {legend}
  </Box>
);

/**
 * core-ui's chart header takes a single node at its right end, which a bar chart already spends on
 * its axis unit — so the unit and the card's controls travel there as one.
 *
 * The unit goes last: core-ui draws the value axis down the right-hand side, and the unit naming
 * it belongs over that axis rather than stranded in the middle of the header.
 */
export const ChartHeaderActions = ({ actions, caption }: { actions: ReactNode; caption?: ReactNode }) => (
  <Box display="flex" alignItems="center" gap={spacing.r8} flex="none">
    {actions}
    {caption}
  </Box>
);

type ChartCardActionsProps = {
  title: string;
  buildExportTable?: () => ExportTable;
  /** Absent once enlarged, and where there is nowhere to enlarge to. */
  onExpand?: () => void;
};

const ChartCardActions = ({ title, buildExportTable, onExpand }: ChartCardActionsProps) => (
  <WidgetActions>
    {buildExportTable && <WidgetExportMenu label={`Export ${title}`} buildTable={buildExportTable} />}
    {onExpand && (
      <WidgetActionButton icon="Expansion" label={`Enlarge ${title}`} tooltip="Enlarge" onClick={onExpand} />
    )}
  </WidgetActions>
);

/**
 * The card a chart widget draws itself in.
 *
 * The in-card plot is deliberately small so a whole dashboard fits on a screen, which leaves
 * nowhere to read a dense per-node series off it — hence an enlarge button that reopens the chart
 * full size in a modal, and an export that hands the plotted numbers over as a file (RD-2364).
 * Enlarging is offered wherever the dual panel is, and withheld where that is: a ~500px embedded
 * sidebar has no room for either. Exporting needs no room, so it is offered everywhere.
 */
export const ChartCard = ({
  title,
  meta,
  buildExportTable,
  children,
}: {
  /** The chart's title. Without one there is no chart worth enlarging, so the button stays off. */
  title?: string;
  /** A line above the enlarged plot — the window it covers, typically. */
  meta?: string;
  /** The plotted numbers, read only once a format is picked. Omit it to offer no export. */
  buildExportTable?: () => ExportTable;
  /**
   * The chart, drawn for the view the card hands it — the same widget renders small in the card
   * and large once enlarged. Plain content for a card with no plot, such as an empty state.
   */
  children: ReactNode | ((view: ChartView) => ReactNode);
}) => {
  const inPanel = useIsInPanel();
  const canEnlarge = useCanSplitLayout();
  const [isExpanded, setIsExpanded] = useState(false);
  const renderChildren = (view: ChartView) => (typeof children === 'function' ? children(view) : children);

  const card = (
    <Card width={inPanel ? '100%' : 'min(600px, 100%)'}>
      <ChartCardBody>
        {renderChildren({
          chartHeight: DEFAULT_CHART_HEIGHT,
          actions: title && (
            <ChartCardActions
              title={title}
              buildExportTable={buildExportTable}
              onExpand={canEnlarge ? () => setIsExpanded(true) : undefined}
            />
          ),
        })}
      </ChartCardBody>
    </Card>
  );

  return (
    <>
      {inPanel ? card : <Box margin={spacing.r16}>{card}</Box>}

      {title && canEnlarge && isExpanded ? (
        <ChartExpandModal title={title} meta={meta} onClose={() => setIsExpanded(false)}>
          {(chartHeight) =>
            renderChildren({
              chartHeight,
              actions: <ChartCardActions title={title} buildExportTable={buildExportTable} />,
            })
          }
        </ChartExpandModal>
      ) : null}
    </>
  );
};
